import {
  Flex,
  Paper,
  Title,
  Text,
  Button,
  Image,
  Skeleton,
} from "@mantine/core";
import { FormattedMessage } from "react-intl";
import {
  isFullCredentials,
  useUpdateCustomerData,
} from "../../../hooks/ui/customerDataStore";
import { usePOAStepsStore } from "../../../hooks/ui/poaStepsDataStore";
import FullmaktskollenImage from "/fullmaktskollen-logo.png";
import { useGetFetchPOAText } from "../../../hooks/api";
import BankIDLogo from "../../../assets/bank-id-logo.svg?react";
import {
  APP_POA_STEP,
  REPLAY_BLOCK_CLASS,
  trackEvent,
} from "../../../utils/statsig";
import dayjs from "dayjs";
import { FooterButtons } from "../../footerButtons";

/** In the app, `onSign` replaces the step advance and `signing` is the wait. */
export default function POASign({
  signing = false,
  onSign,
}: {
  signing?: boolean;
  onSign?: () => void;
}) {
  const { customerData } = useUpdateCustomerData();
  const { nextStep, prevStep, app } = usePOAStepsStore();

  const { data, isSuccess, isLoading } = useGetFetchPOAText({
    credentials: customerData.credentials,
    enabled: isFullCredentials(customerData.credentials),
  });

  const validFrom = dayjs(customerData.validFrom).format("dddd D MMM YYYY");

  const validTo = dayjs(customerData.validTo).format("dddd D MMM YYYY");

  return (
    <Flex justify="center" align="center" data-testid="poa-sign">
      <Flex direction="column" maw={672} w={"100%"}>
        <Title order={2} mb="sm" ff={"var(--wallet-font-family-sambla-sans)"}>
          <FormattedMessage
            id="poaSign.title"
            defaultMessage="Se över dina uppgifter och skriv under med BankID."
          />
        </Title>
        <Text mb={"lg"}>
          <FormattedMessage
            id="poaSign.titleParagraph"
            defaultMessage="Du har alltid möjlighet att ändra dina uppgifter på Fullmaktskollen.se."
          />
        </Text>
        <Paper p={app ? 0 : 10}>
          <Title
            order={3}
            mb={"1rem"}
            ff={"var(--wallet-font-family-sambla-sans)"}
          >
            <FormattedMessage
              id="poaSign.detailsCard.title"
              defaultMessage="Fullmaktsuppgifter"
            />
          </Title>
          <Title
            order={4}
            size="h6"
            style={{ color: "var(--wallet-color-sambla-group-neutrals-500)" }}
          >
            <FormattedMessage
              id="userDetails.personalDetails.email.title"
              defaultMessage="Email"
            />
          </Title>
          <Text mb={"1rem"} className={REPLAY_BLOCK_CLASS}>
            {customerData.email}
          </Text>
          <Title
            order={4}
            size="h6"
            style={{ color: "var(--wallet-color-sambla-group-neutrals-500)" }}
          >
            <FormattedMessage
              id="poaSign.detailsCard.phone.title"
              defaultMessage="Telefon"
            />
          </Title>
          <Text mb={"1rem"} className={REPLAY_BLOCK_CLASS}>
            {customerData.phoneNumber}
          </Text>

          <Title
            order={4}
            size="h6"
            style={{ color: "var(--wallet-color-sambla-group-neutrals-500)" }}
          >
            <FormattedMessage
              id="poaSign.detailsCard.duration.title"
              defaultMessage="Fullmaktens giltighetstid"
            />
          </Title>
          <Text mb={"1rem"}>
            {customerData.validTo === null ? "Tillsvidare" : validFrom}
            {customerData.validTo && <> - {validTo}</>}
          </Text>

          <Title
            order={4}
            size="h6"
            style={{ color: "var(--wallet-color-sambla-group-neutrals-500)" }}
          >
            <FormattedMessage
              id="poaSign.detailsCard.poaHolder"
              defaultMessage="Fullmaktshavare"
            />
          </Title>
          <Text mb={"1rem"}>556974-8378, Sambla Group AB</Text>

          <Title
            order={4}
            size="h6"
            style={{ color: "var(--wallet-color-sambla-group-neutrals-500)" }}
          >
            <FormattedMessage
              id="poaSign.detailsCard.poaGrantor.title"
              defaultMessage="Fullmaktsgivare"
            />
          </Title>
          <Text mb={"1rem"} className={REPLAY_BLOCK_CLASS}>
            {customerData.personalNumber}, {customerData.name}
          </Text>
          {(isLoading || !data) && (
            <>
              <Skeleton h={"20px"} mb={"xs"} />
              <Skeleton h={"20px"} width={"30%"} mb={"xs"} />
              <Skeleton h={"20px"} width={"30%"} mb={"xs"} />
              <Skeleton h={"20px"} width={"30%"} mb={"xs"} />
              <Skeleton h={"20px"} mb={"xs"} />
              <Skeleton h={"20px"} mb={"xs"} />
              <Skeleton h={"20px"} mb={"xs"} />
            </>
          )}
          {isSuccess && data && (
            <div dangerouslySetInnerHTML={{ __html: data.signText }} />
          )}
          <Title
            order={5}
            mb={"1rem"}
            ff={"var(--wallet-font-family-sambla-sans)"}
          >
            <FormattedMessage
              id="poaSign.handlingInfoCard.title"
              defaultMessage="Så hanteras personuppgifter inom Fullmaktskollen"
            />
          </Title>
          <Text mb={"1rem"} style={{ wordBreak: "break-word" }}>
            <FormattedMessage
              id="poaSign.handlingInfoCard.paragraph"
              defaultMessage="Fullmaktskollen i Sverige AB behandlar dina personuppgifter, ditt namn, ditt personnummer, din e-postadress och ditt mobilnummer. Fullmaktskollen behandlar också de personuppgifter som framgår i dina fullmakter. All behandling av dina personuppgifter sker i enlighet med gällande lagstiftning. Syftet med behandlingen är att kunna tillhandahålla tjänsten. För utförligare information om vilka behandlingar som sker av dina personuppgifter se www.fullmaktskollen.se/personuppgifter."
            />
          </Text>
          <Flex justify={"center"} m="2rem">
            <Image
              src={FullmaktskollenImage}
              alt="Fullmaktskollen"
              h={38}
              w="auto"
              fit="contain"
            />
          </Flex>
        </Paper>

        <FooterButtons app={app}>
          <Button
            onClick={() => {
              if (app) {
                trackEvent("pension_app_poa_back_clicked", {
                  step: APP_POA_STEP.sign,
                });
              }
              prevStep();
            }}
            variant="default"
            disabled={signing}
            data-testid="back"
          >
            <FormattedMessage id="bottomNav.back" defaultMessage="Tillbaka" />
          </Button>
          <Button
            onClick={() => {
              if (app) trackEvent("pension_app_poa_sign_clicked");
              (onSign ?? nextStep)();
            }}
            loading={signing}
            disabled={isLoading || !(isSuccess && data)}
            // The label clips it once the app footer splits the row in two.
            rightSection={<BankIDLogo role="img" aria-label="BankID" />}
            data-testid="next"
          >
            <FormattedMessage
              id="bottomNav.sign"
              defaultMessage="Skriv under"
            />
          </Button>
        </FooterButtons>
      </Flex>
    </Flex>
  );
}
